/* global Word, Office */

/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import * as React from "react";
import { createRoot, Root } from "react-dom/client";
import { ConfigProvider } from "antd";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import Home from "@/components/Home";
import RippleProvider from "@/components/ui/RippleProvider";
import "@/globals.css";
import { EnvironmentProvider } from "./context/EnvironmentContext";
import { AuthProvider } from "./context/AuthContext";
import { StyleProvider } from "./context/StyleContext";
import Logger from "@/utility/logger";

/* global document, Office, module, require */
let root: Root | null = null;
const DESKTOP_TASKPANE_WIDTH = 500;

type OfficeWithExtensionLifeCycle = typeof Office & {
  extensionLifeCycle?: Office.ExtensionLifeCycle;
};

const renderApp = (Component: React.ComponentType) => {
  const container = document.getElementById("container");
  if (!container) {
    Logger.error("index.tsx: Container element #container not found in DOM.");
    return;
  }

  if (!root) {
    // Only create the root once
    root = createRoot(container);
  }
  root.render(
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#0069a5",
          colorPrimaryHover: "#19a5cd",
          colorPrimaryActive: "#00527f",
          colorInfo: "#0069a5",
          colorInfoHover: "#19a5cd",
          colorInfoActive: "#00527f",
          colorLink: "#0069a5",
          colorLinkHover: "#19a5cd",
          colorLinkActive: "#00527f",
        },
        components: {
          Button: {
            primaryShadow: "none",
          },
          Select: {
            optionSelectedBg: "rgba(0, 105, 165, 0.1)",
            optionSelectedColor: "#062a3f",
            optionActiveBg: "rgba(25, 165, 205, 0.12)",
          },
          Switch: {
            colorPrimary: "#0069a5",
            colorPrimaryHover: "#19a5cd",
          },
        },
      }}
    >
      <FluentProvider theme={webLightTheme}>
        <RippleProvider>
          <EnvironmentProvider>
            <AuthProvider>
              <StyleProvider>
                <Component />
              </StyleProvider>
            </AuthProvider>
          </EnvironmentProvider>
        </RippleProvider>
      </FluentProvider>
    </ConfigProvider>
  );
};

const setDesktopTaskPaneWidth = (platform: Office.PlatformType) => {
  // Currently this feature doesn't work on Word Web version: https://github.com/OfficeDev/office-js/issues/6530
  // In future when this bug is resolved we won't need the below check anymore
  const isDesktopPlatform = platform === Office.PlatformType.PC || platform === Office.PlatformType.Mac;

  if (!isDesktopPlatform) {
    return;
  }

  try {
    // setWidth is introduced in TaskPaneApi 1.1; higher versions also satisfy this minimum.
    /**
     * Some Word desktop builds may not support TaskPaneApi 1.1 yet, 
     * and so we add this check to ensure the add-in continues to open normally.
     *  
     */ 
    if (!Office.context.requirements.isSetSupported("TaskPaneApi", "1.1")) {
      Logger.debug("index.tsx: TaskPaneApi 1.1 is not supported. Keeping the default task pane width.");
      return;
    }

    const extensionLifeCycle = (Office as OfficeWithExtensionLifeCycle).extensionLifeCycle;

    if (!extensionLifeCycle?.taskpane) {
      Logger.debug("index.tsx: Office.extensionLifeCycle.taskpane is unavailable. Keeping the default task pane width.");
      return;
    }

    extensionLifeCycle.taskpane.setWidth(DESKTOP_TASKPANE_WIDTH);
    Logger.debug(`index.tsx: Desktop task pane width set to ${DESKTOP_TASKPANE_WIDTH}px.`);
  } catch (error) {
    Logger.warn("index.tsx: Failed to set desktop task pane width.");
    Logger.error("index.tsx:", error);
  }
};

/* Render application after Office initializes */
Office.onReady().then((info) => {
  if (info?.platform) {
    setDesktopTaskPaneWidth(info.platform);
  }

  if (info?.host === Office.HostType.Word) {
    if (Office.context?.diagnostics?.version === "0.0.0.0") {
      Logger.warn("index.tsx: Office host version is invalid — Word not fully initialized.");
    } else {
      Logger.debug("index.tsx: Office ready with version:", Office.context?.diagnostics?.version);
      // safe to proceed
    }
  }

  if (Office.context?.diagnostics?.platform === Office.PlatformType.OfficeOnline) {
    const deviceCapabilities = [Office.DevicePermissionType.microphone];
    Office.devicePermission
      ?.requestPermissions(deviceCapabilities)
      ?.then((isGranted) => {
        if (isGranted) {
          Logger.debug("index.tsx: Permission granted.");
          // Reload your add-in before you run code that uses the device capabilities.
          renderApp(Home);
        } else {
          Logger.debug("index.tsx: Permission has been previously granted and is already set in the iframe.");
        }
      })
      .catch((error) => {
        Logger.warn("index.tsx: Permission denied.");
        Logger.error("index.tsx:", error);

        // Do something when permission is denied.
      });
  }

  Logger.debug("index.tsx: Office is ready. Rendering app...");
  if (typeof OfficeExtension !== "undefined" && OfficeExtension?.config) {
    OfficeExtension.config.extendedErrorLogging = true;
  }
  renderApp(Home);

  if (Office.addin?.onVisibilityModeChanged) {
    Office.addin
      .onVisibilityModeChanged((args) => {
        if (args.visibilityMode === Office.VisibilityMode.taskpane) {
          Logger.debug("index.tsx: Task pane became visible again. Re-rendering...");
        }
      })
      .catch((error) => {
        Logger.error("index.tsx: Failed to attach onVisibilityModeChanged handler:", error);
      });
  }
});
